<template>
  <header class="w-full">
    <!-- Main Navbar -->
    <nav class="bg-base-white shadow-sm relative">
      <div class="px-8 lg:px-[80px]">
        <div class="flex items-center h-20">
          <!-- Logo -->
          <router-link to="/" class="flex items-center min-w-[97px]">
            <img v-if="themeStore.current === 'layby'" :src="layupLogo" alt="Layup Logo" class="h-12 lg:h-10 w-auto" />
            <img v-if="themeStore.current === 'boost'" :src="boostLogo" alt="Boost Logo" class="h-12 lg:h-10 w-auto" />
          </router-link>

          <!-- Desktop Navigation -->
          <div class="hidden lg:flex w-full">
            <div class="flex items-center space-x-8 ml-12">
              <router-link
                v-for="link in navLinks"
                :key="link.name"
                :to="link.to"
                class="text-grey-light-600 font-semibold hover:text-primary transition-colors"
              >
                {{ link.name }}
              </router-link>
            </div>
            <div class="ml-auto flex gap-2">
              <a :href="signUpUrl">
                <Button variant="outline" class="text-base">Sign Up</Button>
              </a>
              <a :href="signInUrl">
                <Button>Sign In</Button>
              </a>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <div class="lg:hidden ml-auto">
            <Button variant="ghost" @click="toggleMobileMenu">
              <Menu v-if="!isMobileMenuOpen" class="size-7" />
              <X v-if="isMobileMenuOpen" class="size-7" />
            </Button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <transition @before-enter="beforeEnter" @enter="enter" @leave="leave">
        <div
          v-if="isMobileMenuOpen"
          class="absolute overflow-hidden left-0 right-0 top-full bg-base-white shadow-md px-6 py-4 space-y-4 z-50 lg:hidden"
        >
          <router-link
            v-for="link in navLinks"
            :key="link.name"
            :to="link.to"
            class="block text-grey-light-600 font-semibold hover:text-primary transition-colors"
            @click="isMobileMenuOpen = false"
          >
            {{ link.name }}
          </router-link>
          <SectionDivider />
          <div class="flex flex-col gap-2">
            <a :href="signInUrl">
              <Button variant="outline" class="w-full text-base">Sign In</Button>
            </a>
            <a :href="signUpUrl">
              <Button class="w-full">Sign Up</Button>
            </a>
          </div>
        </div>
      </transition>
    </nav>
  </header>
</template>

<script setup lang="ts">
import boostLogo from '@/assets/images/boost-logo.svg';
import layupLogo from '@/assets/images/layup-logo.svg';
import { DASHBOARDS_URL } from '@/config';
import { useThemeStore } from '@/stores/theme';
import { beforeEnter, enter, leave } from '@/utils/animations';
import { Menu, X } from 'lucide-vue-next';
import type { Ref } from 'vue';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import SectionDivider from './SectionDivider.vue';
import { Button } from './ui/button';

const activeTab = ref<'shoppers' | 'businesses'>('shoppers');
const indicatorPosition = ref<number>(0);
const indicatorWidth = ref<number>(0);
const isMobileMenuOpen = ref<boolean>(false);

const shoppersButtonRef = ref<HTMLButtonElement | null>(null);
const businessesButtonRef = ref<HTMLButtonElement | null>(null);

const themeStore = useThemeStore();

const navLinks = computed(() => {
  if (themeStore.current === 'boost') {
    return [
      { name: 'How It Works', to: { path: '/boost', hash: '#how-it-works' } },
      { name: 'Shop', to: '/boost/stores' },
      { name: 'Help Center', to: '/help-center' },
      { name: 'Contact Us', to: '/' },
    ];
  } else if (themeStore.current === 'layby') {
    return [
      { name: 'How It Works', to: { path: '/layby', hash: '#how-it-works' } },
      { name: 'Shop', to: '/layby/stores' },
      { name: 'Help Center', to: '/help-center' },
      { name: 'Contact Us', to: '/contact-us' },
    ];
  } else {
    // Default/fallback links
    return [
      { name: 'How It Works', to: { path: '/layby', hash: '#how-it-works' } },
      { name: 'Shop', to: '/layby/stores' },
      { name: 'Help Center', to: '/help-center' },
      { name: 'Contact Us', to: '/layup-contact' },
    ];
  }
});

const updateIndicator = async (): Promise<void> => {
  await nextTick();

  const buttonRefs: Record<'shoppers' | 'businesses', Ref<HTMLButtonElement | null>> = {
    shoppers: shoppersButtonRef,
    businesses: businessesButtonRef,
  };

  const currentButtonRef = buttonRefs[activeTab.value];

  if (!currentButtonRef.value) return;

  if (activeTab.value === 'shoppers') {
    indicatorPosition.value = 0;
    indicatorWidth.value = currentButtonRef.value.offsetWidth;
  } else {
    // For businesses tab, position is the width of the shoppers button
    const shoppersWidth = shoppersButtonRef.value?.offsetWidth || 0;
    indicatorPosition.value = shoppersWidth;
    indicatorWidth.value = currentButtonRef.value.offsetWidth;
  }
};

const toggleMobileMenu = (): void => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const signInUrl = computed((): string => {
  return `${DASHBOARDS_URL}/shopper/auth/sign-in`;
});

const signUpUrl = computed((): string => {
  return `${DASHBOARDS_URL}/shopper/auth/sign-up`;
});

onMounted((): void => {
  updateIndicator();
  window.addEventListener('resize', updateIndicator);
});

onUnmounted((): void => {
  window.removeEventListener('resize', updateIndicator);
});
</script>
